import React, { useState, useEffect, useRef } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { ACTION_BLUE } from '../../../theme/theme';

interface CircularProgressWithLabelProps {
    value: number;
    timeLeft: string; // Changed to string to display formatted time
}

function CircularProgressWithLabel({
                                       value,
                                       timeLeft,
                                   }: CircularProgressWithLabelProps) {
    return (
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
                variant="determinate"
                value={value}
                size={40}
                sx={{
                    color: ACTION_BLUE,
                }}
            />
            <Box
                sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {/* sx, not the `color` prop: that only accepts a palette key
                    or a CSS colour, so the literal "ACTION_BLUE" was ignored. */}
                <Typography
                    variant="caption"
                    component="div"
                    sx={{ color: ACTION_BLUE }}
                >
                    {timeLeft}
                </Typography>
            </Box>
        </Box>
    );
}

interface CircularTimerProps {
    totalTime: number;
    onComplete: () => void;
}

const CircularTimer: React.FC<CircularTimerProps> = ({
                                                         totalTime,
                                                         onComplete,
                                                     }) => {
    const [timeLeft, setTimeLeft] = useState<number>(totalTime);

    // The parent declares onComplete inline, so it is a new function on every
    // render. Holding it in a ref keeps it out of the interval effect's
    // dependencies: listing it there tore the interval down and rebuilt it on
    // each render, so the countdown restarted and never reached zero.
    const onCompleteRef = useRef(onComplete);

    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);

    useEffect(() => {
        setTimeLeft(totalTime);
    }, [totalTime]);

    useEffect(() => {
        if (timeLeft <= 0) {
            return;
        }

        const timer = setInterval(() => {
            setTimeLeft((prevTime) => (prevTime <= 1 ? 0 : prevTime - 1));
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft]);

    // Fired from an effect rather than inside the state updater, which would
    // be a side effect during render.
    useEffect(() => {
        if (timeLeft === 0) {
            onCompleteRef.current();
        }
    }, [timeLeft]);

    // Format time as MM:SS
    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const progressValue = totalTime > 0 ? (timeLeft / totalTime) * 100 : 0;

    return (
        <CircularProgressWithLabel
            value={progressValue}
            timeLeft={formatTime(timeLeft)}
        />
    );
};

export default CircularTimer;
